import { Box, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { IAppointmentView, appointmentService } from '../../../../services';
import { SchedulerFilterOptionsEnum } from '../Scheduler';

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MAX_CHIPS_PER_DAY = 3;

interface IProps {
  businessId: number | undefined;
  filterOptionType: number;
  option: { id: number; name: string } | undefined;
  date: Date;
  refetch: () => any;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onDayClick: (day: Date) => void;
}

export const MonthView: React.FC<IProps> = ({
  businessId,
  filterOptionType,
  option,
  date,
  setIsLoading,
  onDayClick,
}) => {
  const theme = useTheme();

  // First and last day of the displayed month
  const { firstDay, lastDay } = useMemo(() => {
    const first = new Date(date.getFullYear(), date.getMonth(), 1);
    const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { firstDay: first, lastDay: last };
  }, [date]);

  // Build the 6-week grid cells (may include days from prev/next month for alignment)
  const gridDays = useMemo(() => {
    const cells: Date[] = [];
    const startOffset = firstDay.getDay(); // 0=Sun
    // Pad with days from the previous month
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(firstDay);
      d.setDate(d.getDate() - i - 1);
      cells.push(d);
    }
    // Days of current month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      cells.push(new Date(date.getFullYear(), date.getMonth(), d));
    }
    // Pad remainder to complete the last row
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const d = new Date(lastDay);
        d.setDate(d.getDate() + i);
        cells.push(d);
      }
    }
    return cells;
  }, [firstDay, lastDay, date]);

  const { data: appointments, isFetching } = useQuery(
    ['appointments', businessId, option, firstDay, lastDay],
    () =>
      appointmentService.get(
        filterOptionType === SchedulerFilterOptionsEnum.Business ? option?.id : businessId || undefined,
        filterOptionType === SchedulerFilterOptionsEnum.Employee ? option?.id : undefined,
        firstDay,
        lastDay,
        true
      ),
    { enabled: !!date && (!!businessId || !!option) }
  );

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  // Group appointments by local date string "YYYY-MM-DD"
  const appointmentsByDay = useMemo(() => {
    const map = new Map<string, IAppointmentView[]>();
    (appointments || []).forEach((appt) => {
      const d = new Date(appt.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(appt);
    });
    return map;
  }, [appointments]);

  const toKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const todayKey = toKey(new Date());
  const currentMonthKey = `${date.getFullYear()}-${date.getMonth()}`;

  return (
    <Box sx={{ width: '100%', userSelect: 'none' }}>
      {/* Day-of-week header */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(7, 1fr)"
        sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 0.5 }}
      >
        {DAY_NAMES.map((name) => (
          <Box key={name} textAlign="center" py={0.75}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}
            >
              {name}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" sx={{ gap: '2px' }}>
        {gridDays.map((day, idx) => {
          const dayKey = toKey(day);
          const isToday = dayKey === todayKey;
          const isCurrentMonth = `${day.getFullYear()}-${day.getMonth()}` === currentMonthKey;
          const dayAppts = appointmentsByDay.get(dayKey) || [];
          const visible = dayAppts.slice(0, MAX_CHIPS_PER_DAY);
          const overflow = dayAppts.length - visible.length;

          return (
            <Box
              key={idx}
              onClick={() => isCurrentMonth && onDayClick(day)}
              sx={{
                minHeight: 100,
                p: 0.75,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: isToday ? 'primary.main' : 'divider',
                backgroundColor: isToday
                  ? `${theme.palette.primary.main}18`
                  : isCurrentMonth
                    ? 'background.paper'
                    : 'action.hover',
                cursor: isCurrentMonth ? 'pointer' : 'default',
                opacity: isCurrentMonth ? 1 : 0.45,
                transition: 'all 0.15s ease',
                '&:hover': isCurrentMonth
                  ? {
                    backgroundColor: `${theme.palette.primary.main}22`,
                    borderColor: 'primary.light',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  }
                  : {},
              }}
            >
              {/* Day number */}
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isToday ? 'primary.main' : 'transparent',
                  color: isToday ? 'primary.contrastText' : 'text.primary',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: isToday ? 700 : 500, fontSize: '0.78rem' }}>
                  {day.getDate()}
                </Typography>
              </Box>

              {/* Appointment chips */}
              <Box display="flex" flexDirection="column" gap={0.3}>
                {visible.map((appt) => {
                  const label = `${appt.startTime} ${appt.customerName}`;
                  return (
                    <Tooltip key={appt.id} title={`${appt.customerName} | ${appt.employeeName} | ${appt.startTime}–${appt.endTime}`}>
                      <Chip
                        label={label.length > 18 ? label.substring(0, 18) + '…' : label}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          borderRadius: '4px',
                          backgroundColor: appt.canceled ? theme.palette.grey[500] : appt.employeeColor || theme.palette.primary.main,
                          color: '#fff',
                          textDecoration: appt.canceled ? 'line-through' : 'none',
                          opacity: appt.canceled ? 0.6 : 1,
                          '& .MuiChip-label': { px: 0.75, py: 0 },
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  );
                })}

                {overflow > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem', pl: 0.25 }}>
                    +{overflow} mais
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
